import React, { useEffect, useState } from 'react';
import FixedShuttlebus from './FixedShuttle/FixedShuttlebus';
import LoopedShuttlebus from './LoopedShuttle/LoopedShuttlebus';
import LiveDispatch from './LiveDispatch/LiveDispatch';
import comms from '../comms';

function Shuttlebus({ 
  socket: passedSocket, 
  navigateTo, 
  userSettings: propUserSettings,
  driverAuthData,
  fleetSettings: propFleetSettings,
  selectedVehicle: propSelectedVehicle,
  routeDetails: propRouteDetails,
  scheduleType: propScheduleType, // Schedule type is now passed from parent
  onRouteUpdate // Callback to notify parent of route updates
}) {
  const [error, setError] = useState(null);
  const [driverData, setDriverData] = useState(driverAuthData || null);
  const [currentRouteDetails, setCurrentRouteDetails] = useState(propRouteDetails);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [hasSocketUpdate, setHasSocketUpdate] = useState(false);

  // Update currentRouteDetails when propRouteDetails changes, but only if not from socket
  useEffect(() => {
    if (propRouteDetails && JSON.stringify(propRouteDetails) !== JSON.stringify(currentRouteDetails)) {
      // Only update if this isn't a socket update
      if (!hasSocketUpdate) {
        setCurrentRouteDetails(propRouteDetails);
      }
    }
  }, [propRouteDetails, currentRouteDetails, hasSocketUpdate]);

  // Initialize with route data from authorization
  useEffect(() => {
    if (!propFleetSettings) {
      setError('Fleet settings not found');
      return;
    }

    // Socket handling for real-time updates
    const initializeSocket = async () => {
      let socket = passedSocket;
      
      if (!socket || typeof socket.on !== 'function') {
        socket = comms.getSocket();
        
        if (!socket || typeof socket.on !== 'function') {
          try {
            socket = await comms.connectSocket(propFleetSettings.fleetUrl || propFleetSettings.dispatchUri);
          } catch (error) {
            console.error('Failed to create socket connection:', error);
            // Don't treat socket failure as critical error for shuttlebus
            setIsSocketReady(true); // Allow component to continue without socket
            return;
          }
        }
      }

      if (socket && typeof socket.on === 'function') {
        const handleSocketMessage = (msg) => {
          try {
            if (!msg || typeof msg !== 'string') return;

            const messageParts = msg.split("|");
            
            if (messageParts[0]) {
              comms.sendSocket("ACK|" + messageParts[0]);
            }

            if (messageParts[1] === 'FIXEDROUTE_UPDATE' && messageParts[2]) {
              try {
                const msgObj = JSON.parse(messageParts[2]);
                setDriverData(msgObj);
                setCurrentRouteDetails(msgObj.fixedRoute || msgObj);
                setHasSocketUpdate(true);
                
                // Notify parent of route update
                if (onRouteUpdate && msgObj.fixedRoute) {
                  onRouteUpdate(msgObj.fixedRoute);
                }
              } catch (parseError) {
                console.warn('Failed to parse message JSON:', messageParts[2], parseError);
              }
            }
          } catch (error) {
            console.error('Error processing router socket message:', error);
          }
        };

        socket.on('Command', handleSocketMessage);
        setIsSocketReady(true);

        return () => {
          if (socket && typeof socket.off === 'function') {
            socket.off('Command', handleSocketMessage);
          }
        };
      } else {
        setIsSocketReady(true); // Allow component to continue without socket
      }
    };

    initializeSocket();

  }, [passedSocket, propFleetSettings, onRouteUpdate]);

  if (error) {
    return (
      <div className="bg-slate-100 flex items-center justify-center" style={{ height: `calc(100vh - 65px)` }}>
        <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Configuration Error</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Don't render child components until socket is ready and we have initial route data
  if (!isSocketReady || !currentRouteDetails) {
    return (
      <div className="bg-slate-100 flex items-center justify-center" style={{ height: `calc(100vh - 65px)` }}>
        <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-md">
          <div className="mb-4">
            <div className="w-8 h-8 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Initializing Route</h3>
          <p className="text-gray-600">Setting up connection and loading route data...</p>
        </div>
      </div>
    );
  }

  // Route to appropriate component based on schedule type (determined during authorization)
  if (propScheduleType === 'looped') {
    return (
      <LoopedShuttlebus 
        socket={passedSocket}
        fleetSettings={propFleetSettings}
        userSettings={propUserSettings}
        driverData={driverData}
        navigateTo={navigateTo}
        routeDetails={currentRouteDetails}
        selectedVehicle={propSelectedVehicle}
        onRouteUpdate={(updatedRoute) => {
          // Update the current route details with the new itinerary
          if (updatedRoute) {
            setCurrentRouteDetails(prevDetails => {
              // Create the updated route details
              const updated = {
                ...prevDetails,
                ...(updatedRoute.itinerary && { itinerary: updatedRoute.itinerary }),
                ...(updatedRoute.daySchedule && { itinerary: updatedRoute.daySchedule }),
                 breakLogs: updatedRoute.breakLogs, 
                lastUpdated: updatedRoute.lastUpdated || new Date().toISOString(),
                updateSource: updatedRoute.updateSource || 'childComponent'
              };
              
              // Also notify parent component if callback is provided
              if (onRouteUpdate && typeof onRouteUpdate === 'function') {
                onRouteUpdate(updated);
              }
              
              return updated;
            });
          }
        }}
      />
    );
  }

  if (propScheduleType === 'fixed') {
    return (
      <FixedShuttlebus 
        socket={passedSocket}
        fleetSettings={propFleetSettings}
        userSettings={propUserSettings}
        selectedVehicle={propSelectedVehicle}
        driverData={driverData}
        navigateTo={navigateTo}
        routeDetails={currentRouteDetails}
        onRouteUpdate={(updatedRoute) => {
          // Update the current route details with the new itinerary
          if (updatedRoute) {
            setCurrentRouteDetails(prevDetails => {
              // Create the updated route details
              const updated = {
                ...prevDetails,
                ...(updatedRoute.itinerary && { itinerary: updatedRoute.itinerary }),
                breakLogs: updatedRoute.breakLogs, // Include break logs if provided
                lastUpdated: updatedRoute.lastUpdated || new Date().toISOString(),
                updateSource: updatedRoute.updateSource || 'onRouteUpdate'
              };
              
              // Also notify parent component if callback is provided
              if (onRouteUpdate && typeof onRouteUpdate === 'function') {
                onRouteUpdate(updated);
              }
              
              return updated;
            });
          }
        }}
      />
    );
  }

  // For dispatch mode or fallback, render the LiveDispatch component
  return (
    <LiveDispatch 
      navigateTo={navigateTo}
      userSettings={propUserSettings}
      fleetSettings={propFleetSettings}
      socket={passedSocket}
    />
  );
}


export default Shuttlebus;
